/**
 * @page Presentation
 * @route /presentation (public)
 * @description Tour de feature YUMIX en mode "command-center" — Clean & Glow.
 *
 * Refonte complète Feb 2026 (Yuri Iter. 310). Remplace le slideshow auto-play
 * par une page scrollable single-shot qui :
 *   1. Reflète exactement les capacités réelles de l'app (alignée avec /guide)
 *   2. Reprend la signature Clean & Glow du dashboard (rouge racing, gold,
 *      mauve mindset, cyan FatSecret, émeraude HUD)
 *   3. Convertit : 2 CTAs persistants (Démarrer essai · Lire le Guide)
 *
 * Sections (ancres scroll) :
 *   - Hero            : Logo pulsant + tagline + CTA prinipaux
 *   - HUD             : Mission Tracker + Triple Victoire (rouge racing)
 *   - Mindset         : 4ème pilier IA proactive (mauve)
 *   - Précision       : FatSecret Vérifié + Top Précision (cyan)
 *   - Badges          : 39 badges tactiques (or)
 *   - Modes           : Clinique / Hormonal / Religieux / Neuro (émeraude)
 *   - Coach YUMIX        : Friction Zéro + voix (orange)
 *   - Footer CTA      : Démarrer + lien Guide
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Radio, Trophy, Brain, ShieldCheck, Crown, Stethoscope,
  MessageSquare, ArrowRight, Sparkles, Footprints, Zap,
  Heart, Moon, Users, BookOpen, ChefHat,
  Compass, Mic, ChevronRight, Star, Globe, ShieldAlert,
  Gift, Share2, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { captureRefFromUrl, getStoredRef, shareYumix } from '../utils/referral';

// Iter. 411.r — ES translation map + `T()` helper.
// Usage: T('French text', 'English text') — returns the language-appropriate
// string based on `_pageLang` (set to `lang` at Presentation() mount). Falls
// back to EN when the requested ES string isn't in the map.
let _pageLang = 'fr';
const ES_MAP = {
  'Invited by': 'Invitado por',
  'Read the YUMIX Guide': 'Leer la Guía YUMIX',
  'Link copied': 'Enlace copiado',
  'Share YUMIX': 'Compartir YUMIX',
  '3 pillars, 1 dopamine loop.': '3 pilares, 1 ciclo dopaminérgico.',
  'HUD Mission Tracker at the top of dashboard, live': 'HUD Mission Tracker arriba del dashboard, en vivo',
  'Global sound mute via localStorage if you prefer silence': 'Silencio global vía localStorage si prefieres el silencio',
  'The 4th pillar. The one nobody else covers.': 'El 4º pilar. El que nadie más cubre.',
  'Daily': 'Diario',
  'Proactive morning banner — instant prefetched AI feedback.': 'Banner matinal proactivo — feedback IA prefetch instantáneo.',
  'Weekly Recap': 'Resumen semanal',
  'Glowing card summarizing the week\'s evolution.': 'Tarjeta luminosa que resume la evolución de la semana.',
  'Monthly Recap': 'Resumen mensual',
  'Monthly synthesis of your mental OS — detected patterns.': 'Síntesis mensual de tu OS mental — patrones detectados.',
  'Validated macros. Not estimated.': 'Macros validados. No estimados.',
  'Verified': 'Verificado',
  'Official source': 'Fuente oficial',
  'Packaged products match in milliseconds. Cyan badge = official source.': 'Productos envasados se identifican en milisegundos. Insignia cian = fuente oficial.',
  'Top Precision 🇨🇦': 'Precisión Máxima 🇨🇦',
  'Walking': 'Marcha',
  'Fuel': 'Combustible',
  'Forge': 'Forja',
  'Citadel': 'Ciudadela',
  'Ranks': 'Rangos',
  'View my badges': 'Ver mis insignias',
  'Clinical': 'Clínico',
  'Injury mode: calories ↓ + repair nutrients ↑': 'Modo lesión: calorías ↓ + nutrientes de reparación ↑',
  'Hormonal': 'Hormonal',
  'Menstrual cycle, perimenopause, cortisol/insulin optim.': 'Ciclo menstrual, perimenopausia, optim. cortisol/insulina.',
  'Religious': 'Religioso',
  'Ramadan, Halal, Kosher, Lent, Hindu — auto filters.': 'Ramadán, Halal, Kosher, Cuaresma, Hindú — filtros automáticos.',
  'Neuro': 'Neuro',
  'MIND diet for seniors (-53% cognitive decline risk)': 'Dieta MIND para mayores (-53% riesgo de deterioro cognitivo)',
  'Activate one or more modes based on your life. YUMIX adjusts macros, recipes, filtered deals and even its language.': 'Activa uno o varios modos según tu vida. YUMIX ajusta macros, recetas, ofertas filtradas e incluso su idioma.',
  'Talk. It acts.': 'Habla. Actúa.',
  'Plate Photo': 'Foto del plato',
  'Snap → analysis + macros + 4-pillar verdict in 1 prompt.': 'Foto → análisis + macros + veredicto 4 pilares en 1 prompt.',
  'Voice Mode': 'Modo Voz',
  'Whisper STT + spoken response. Coach in your pocket.': 'Whisper STT + respuesta hablada. Coach en tu bolsillo.',
  'Action Buttons': 'Botones de acción',
  '1 tap → pre-filled page. Secure allowlist.': '1 tap → página prellenada. Allowlist segura.',
  'Walking Command Center': 'Centro de Comando Marcha',
  'AI Recipes + My Recipes': 'Recetas IA + Mis Recetas',
  'Family Profile (auto portions)': 'Perfil Familiar (porciones automáticas)',
  'Zero Relay Legal Shield (18+ safety)': 'Zero Relay Legal Shield (seguridad 18+)',
  'Extra Miles community + Pack Clash': 'Comunidad Extra Miles + Clash de Manadas',
  'Everything is wired together.': 'Todo está conectado.',
  'Ready?': '¿Listo?',
  'Mission #01 — boot.': 'Misión #01 — inicio.',
  'Read the Guide': 'Leer la Guía',
  'YUMIX · Powered by Extra Miles · Made in Québec': 'YUMIX · Impulsado por Extra Miles · Hecho en Quebec',
  'Start': 'Comenzar',
  // Iter. 411.r — additional ES entries for strings with inner apostrophes
  // that the initial regex sweep couldn't grab.
  'Start the experience': 'Comenzar la experiencia',
  'Triple-hit = pulsing gold halo + "Perfect day!" banner': 'Triple-hit = halo dorado pulsante + banner "¡Día perfecto!"',
  'Users ranked by verified-meals percentage. Pushes precision without obsession.': 'Usuarios clasificados por % de comidas verificadas. Impulsa la precisión sin obsesión.',
  'From Cadet to Operation Apollo.': 'De Cadete a Operación Apolo.',
  'AI adapts to YOU. Not the other way around.': 'La IA se adapta a TI. No al revés.',
  'Talk to the Assistant': 'Hablar con el Asistente',
};
function T(fr_str, en_str) {
  if (_pageLang === 'fr') return fr_str;
  if (_pageLang === 'es') return ES_MAP[en_str] || en_str;
  return en_str;
}


// ───── Reusable atoms (kept inline to avoid one-shot util files) ─────

function GlowCard({ children, accent = 'orange', testId }) {
  // accent → tuple [borderRgba, shadowRgba]
  const palette = {
    orange:  ['rgba(251,146,60,0.40)',  'rgba(251,146,60,0.25)'],
    red:     ['rgba(239,68,68,0.55)',   'rgba(239,68,68,0.30)'],
    gold:    ['rgba(251,191,36,0.55)',  'rgba(251,191,36,0.30)'],
    violet:  ['rgba(167,139,250,0.50)', 'rgba(167,139,250,0.28)'],
    cyan:    ['rgba(34,211,238,0.50)',  'rgba(34,211,238,0.28)'],
    emerald: ['rgba(52,211,153,0.50)',  'rgba(52,211,153,0.28)'],
    rose:    ['rgba(244,114,182,0.50)', 'rgba(244,114,182,0.28)'],
  }[accent] || ['rgba(251,146,60,0.40)', 'rgba(251,146,60,0.25)'];
  return (
    <div
      data-testid={testId}
      className="relative rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-zinc-950/80 via-black/85 to-zinc-950/70 backdrop-blur-sm transition-transform hover:-translate-y-0.5"
      style={{
        border: `1px solid ${palette[0]}`,
        boxShadow: `0 0 22px ${palette[1]}, inset 0 0 0 1px rgba(255,255,255,0.04)`,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children, accent = 'orange' }) {
  const color = {
    orange: '#fb923c', red: '#ef4444', gold: '#fbbf24',
    violet: '#a78bfa', cyan: '#22d3ee', emerald: '#34d399', rose: '#f472b6',
  }[accent] || '#fb923c';
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-mono"
      style={{
        background: `${color}1A`,
        border: `1px solid ${color}66`,
        boxShadow: `0 0 10px ${color}55`,
      }}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
          style={{ background: color }} />
        <span className="relative inline-flex rounded-full h-2 w-2"
          style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
      </span>
      <span className="text-[10px] font-black tracking-[0.28em] uppercase"
        style={{ color, textShadow: `0 0 8px ${color}99` }}>
        {children}
      </span>
    </div>
  );
}

function ScanlineOverlay() {
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-25"
      style={{
        background: 'repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(251,146,60,0.06) 3px, rgba(251,146,60,0.06) 4px)',
      }} />
  );
}

/**
 * Invitation ribbon — shown in the hero when the visitor arrived via a
 * referral link (?ref=<id>). Gentle social-proof signal without blocking
 * the scroll.
 */
function InvitationRibbon({ refId, fr }) {
  if (!refId) return null;
  return (
    <div
      data-testid="presentation-invitation-ribbon"
      className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full font-mono"
      style={{
        background: 'linear-gradient(90deg, rgba(244,114,182,0.12), rgba(251,146,60,0.12))',
        border: '1px solid rgba(244,114,182,0.45)',
        boxShadow: '0 0 14px rgba(244,114,182,0.35)',
      }}
    >
      <Gift className="w-3.5 h-3.5 text-rose-300" />
      <span className="text-[11px] font-black uppercase tracking-[0.22em] text-rose-200"
        style={{ textShadow: '0 0 6px rgba(244,114,182,0.55)' }}>
        {T('Invité par', 'Invited by')} <span className="text-orange-300">{refId}</span>
      </span>
    </div>
  );
}

// ───── Section components ─────

function HudPreview() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const hh = String(time.getHours()).padStart(2, '0');
  const mm = String(time.getMinutes()).padStart(2, '0');
  return (
    <div className="rounded-xl overflow-hidden relative font-mono"
      style={{
        background: 'linear-gradient(90deg, rgba(10,10,10,0.96), rgba(15,15,20,0.96), rgba(10,10,10,0.96))',
        border: '1px solid rgba(251,146,60,0.40)',
        boxShadow: '0 0 18px rgba(251,146,60,0.30), inset 0 0 0 1px rgba(251,146,60,0.10)',
      }}
      data-testid="presentation-hud-preview"
    >
      <ScanlineOverlay />
      <div className="relative flex items-center gap-3 px-4 py-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 bg-emerald-400" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"
            style={{ boxShadow: '0 0 8px rgba(52,211,153,0.95)' }} />
        </span>
        <Radio className="w-3.5 h-3.5 text-orange-400/80" />
        <span className="text-sm font-black tabular-nums tracking-widest text-orange-300"
          style={{ textShadow: '0 0 8px rgba(251,146,60,0.55)' }}>
          {hh}:{mm}
        </span>
        <span className="text-orange-500/50">·</span>
        <span className="text-[12px] font-black uppercase tracking-[0.22em] text-amber-300"
          style={{ textShadow: '0 0 8px rgba(251,191,36,0.55)' }}>
          MISSION #07
        </span>
        <span className="text-orange-500/50 hidden sm:inline">·</span>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-emerald-300"
          style={{ textShadow: '0 0 8px rgba(52,211,153,0.45)' }}>
          <Zap className="w-3 h-3" /> 2 PILIERS PRIORITAIRES
        </span>
      </div>
    </div>
  );
}

function TripleVictoryPreview() {
  return (
    <div className="rounded-2xl p-4 relative"
      style={{
        background: 'linear-gradient(135deg, rgba(127,29,29,0.55), rgba(0,0,0,0.80), rgba(127,29,29,0.40))',
        border: '2px solid rgba(239,68,68,0.60)',
        boxShadow: '0 0 20px rgba(239,68,68,0.55), inset 0 0 0 1px rgba(239,68,68,0.20)',
      }}
      data-testid="presentation-triple-victory-preview"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-red-400" style={{ filter: 'drop-shadow(0 0 5px rgba(239,68,68,0.7))' }} />
          <h3 className="font-heading text-sm uppercase tracking-wider text-foreground">Triple Victoire</h3>
        </div>
        <span className="text-[10px] font-black tracking-widest text-red-400">2/3</span>
      </div>
      <div className="space-y-3">
        {[
          { label: 'Pas',       value: '11 312 / 10 000', pct: 100, hit: true },
          { label: 'Protéines', value: '124 g / 110 g',   pct: 100, hit: true },
          { label: 'Entraînement', value: 'À faire',     pct: 30,  hit: false },
        ].map((row) => (
          <div key={row.label}>
            <div className="flex justify-between text-[10px] uppercase tracking-widest mb-1">
              <span className={row.hit ? 'text-amber-400' : 'text-muted-foreground'}>{row.label}</span>
              <span className={row.hit ? 'text-amber-300 font-black' : 'text-foreground font-bold'}>{row.value}</span>
            </div>
            <div className="relative h-2.5 w-full rounded-full bg-black/90 ring-1 ring-red-500/55"
              style={{
                boxShadow: row.hit ? 'inset 0 0 6px rgba(0,0,0,0.6)' : '0 0 12px rgba(239,68,68,0.55), inset 0 0 6px rgba(0,0,0,0.65)',
              }}
            >
              <div className="h-full rounded-full"
                style={{
                  width: `${Math.max(row.pct, 6)}%`,
                  background: row.hit
                    ? 'linear-gradient(to right, #ea580c, #f97316, #fcd34d)'
                    : 'linear-gradient(to right, #fb7185, #ef4444, #f87171)',
                  boxShadow: row.hit
                    ? '0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45)'
                    : '0 0 18px rgba(248,113,113,1), 0 0 8px rgba(239,68,68,0.9)',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionHero({ navigate, fr, lang, refId, onShare, shareState }) {
  return (
    <section className="relative pt-16 sm:pt-24 pb-20 px-4">
      {/* radial bg */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top, rgba(251,146,60,0.18), transparent 60%), radial-gradient(ellipse at bottom, rgba(167,139,250,0.10), transparent 70%)',
        }} />
      <div className="max-w-5xl mx-auto relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="flex flex-col items-start gap-6">
          {refId
            ? <InvitationRibbon refId={refId} fr={fr} />
            : <SectionLabel accent="orange">YUMIX · Présentation v5.1</SectionLabel>}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tight text-foreground leading-[0.92]">
            {lang === 'es' ? (<>Tu cockpit de salud,<br /><span className="bg-gradient-to-r from-orange-500 via-rose-400 to-amber-300 bg-clip-text text-transparent">en modo misión.</span></>)
             : fr ? (<>Ton cockpit de santé,<br /><span className="bg-gradient-to-r from-orange-500 via-rose-400 to-amber-300 bg-clip-text text-transparent">en mode mission.</span></>)
                : (<>Your health cockpit,<br /><span className="bg-gradient-to-r from-orange-500 via-rose-400 to-amber-300 bg-clip-text text-transparent">in mission mode.</span></>)}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            {lang === 'es'
              ? "Foto del plato, IA proactiva, gamificación dopaminérgica, base nutricional verificada de Canadá. YUMIX condensa el rigor de un coach privado en una interfaz diseñada para que una rutina se ancle — y no vuelva a caer."
              : fr
              ? "Photo de l'assiette, IA proactive, gamification dopaminergique, base nutrition vérifiée Canada. YUMIX condense la rigueur d'un coach privé dans une interface pensée pour qu'une routine s'ancre — et qu'elle ne tombe plus."
              : "Plate photo, proactive AI, dopaminergic gamification, verified Canadian nutrition DB. YUMIX condenses the rigor of a private coach into an interface built so a routine sticks — and stops falling apart."}
          </p>

          {/* Live HUD demo */}
          <div className="w-full mt-2 max-w-3xl">
            <HudPreview />
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3 mt-4 items-center">
            <Button data-testid="presentation-cta-start" onClick={() => navigate('/')}
              className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-black uppercase tracking-widest text-xs px-6 py-6 rounded-full"
              style={{ boxShadow: '0 0 22px rgba(251,146,60,0.55)' }}>
              {T("Démarrer l'expérience", 'Start the experience')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button data-testid="presentation-cta-guide" onClick={() => navigate('/guide')}
              variant="outline"
              className="border-orange-500/50 text-orange-300 hover:bg-orange-500/15 font-bold uppercase tracking-widest text-xs px-6 py-6 rounded-full">
              <Compass className="w-4 h-4 mr-2" />
              {T('Lire le Guide YUMIX', 'Read the YUMIX Guide')}
            </Button>
            <Button data-testid="presentation-cta-share" onClick={onShare}
              variant="ghost"
              className="text-rose-300 hover:bg-rose-500/15 hover:text-rose-200 font-bold uppercase tracking-widest text-xs px-5 py-6 rounded-full">
              {shareState === 'copied'
                ? (<><Check className="w-4 h-4 mr-2" />{T('Lien copié', 'Link copied')}</>)
                : (<><Share2 className="w-4 h-4 mr-2" />{T('Partager YUMIX', 'Share YUMIX')}</>)}
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SectionMission({ fr }) {
  return (
    <section className="relative py-16 px-4 border-t border-zinc-900/50" data-testid="presentation-section-mission">
      <div className="max-w-5xl mx-auto">
        <SectionLabel accent="red">01 · Triple Victoire — Rouge Racing</SectionLabel>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mt-4 mb-3">
          {T("3 piliers, 1 boucle dopaminergique.", '3 pillars, 1 dopamine loop.')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
          {fr
            ? "Pas, Protéines, Entraînement. Tant que la cible n'est pas atteinte, la barre est rouge fluo glow. À l'instant où tu valides, micro-vroom racing + vibration haptique, et la barre bascule en or. Ton cerveau enregistre la victoire."
            : "Steps, Protein, Workout. Until target hit, bar is fluorescent red glow. The moment you validate: racing micro-vroom + haptic vibration, bar flips to gold. Your brain registers the win."}
        </p>
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <TripleVictoryPreview />
          <div className="space-y-4">
            {[
              { icon: Trophy,     t: T('Triple-hit = halo or pulsant + bannière "Journée parfaite !"', 'Triple-hit = pulsing gold halo + "Perfect day!" banner') },
              { icon: Radio,      t: T('HUD Mission Tracker en haut du dashboard, live (clock + N piliers prioritaires)', 'HUD Mission Tracker at the top of dashboard, live') },
              { icon: Zap,        t: T('Mute global du son via localStorage si tu préfères le silence', 'Global sound mute via localStorage if you prefer silence') },
            ].map(({ icon: Icon, t }, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="mt-0.5 w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center"
                  style={{ boxShadow: '0 0 10px rgba(239,68,68,0.35)' }}>
                  <Icon className="w-4 h-4 text-red-400" />
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionMindset({ fr }) {
  return (
    <section className="relative py-16 px-4 border-t border-zinc-900/50" data-testid="presentation-section-mindset">
      <div className="max-w-5xl mx-auto">
        <SectionLabel accent="violet">02 · Mindset OS — IA Proactive</SectionLabel>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mt-4 mb-3">
          {T("Le 4ème pilier. Celui que personne ne traite.", 'The 4th pillar. The one nobody else covers.')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
          {fr
            ? "Énergie, stress, focus, gratitude. Gemini 3 Flash analyse en arrière-plan ton historique mental et te livre un debrief personnalisé en 3 phrases — comme un coach de haut niveau qui te connaît. Recap hebdo et mensuel pour voir l'évolution."
            : 'Energy, stress, focus, gratitude. Gemini 3 Flash analyzes your mental history in the background and delivers a personalized 3-sentence debrief.'}
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <GlowCard accent="violet" testId="card-mindset-daily">
            <Brain className="w-6 h-6 text-violet-400 mb-3" style={{ filter: 'drop-shadow(0 0 6px rgba(167,139,250,0.7))' }} />
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-1">{T('Daily', 'Daily')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T('Bannière proactive matin — feedback IA instantané prefetché.', 'Proactive morning banner — instant prefetched AI feedback.')}</p>
          </GlowCard>
          <GlowCard accent="violet" testId="card-mindset-weekly">
            <Star className="w-6 h-6 text-violet-400 mb-3" style={{ filter: 'drop-shadow(0 0 6px rgba(167,139,250,0.7))' }} />
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-1">{T('Recap Hebdo', 'Weekly Recap')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T('Carte glowing résumant l\'évolution de la semaine.', 'Glowing card summarizing the week\'s evolution.')}</p>
          </GlowCard>
          <GlowCard accent="violet" testId="card-mindset-monthly">
            <Sparkles className="w-6 h-6 text-violet-400 mb-3" style={{ filter: 'drop-shadow(0 0 6px rgba(167,139,250,0.7))' }} />
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-1">{T('Recap Mensuel', 'Monthly Recap')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T('Synthèse mensuelle de ton OS mental — patterns détectés.', 'Monthly synthesis of your mental OS — detected patterns.')}</p>
          </GlowCard>
        </div>
      </div>
    </section>
  );
}

function SectionPrecision({ fr }) {
  return (
    <section className="relative py-16 px-4 border-t border-zinc-900/50" data-testid="presentation-section-precision">
      <div className="max-w-5xl mx-auto">
        <SectionLabel accent="cyan">03 · FatSecret Vérifié — Top Précision 🇨🇦</SectionLabel>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mt-4 mb-3">
          {T("Macros validées. Pas estimées.", 'Validated macros. Not estimated.')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
          {fr
            ? "Chaque repas est d'abord cherché dans la base FatSecret Canada (vraies données nutrition). Trouvé → badge cyan « VÉRIFIÉ ». Pas trouvé → fallback IA Gemini transparent. Le leaderboard Top Précision classe les utilisateurs selon leur % de repas vérifiés."
            : "Every meal is first searched in the FatSecret Canada DB. Found → cyan 'VERIFIED' badge. Not found → transparent Gemini AI fallback. The Top Precision leaderboard ranks users by their verified-meals percentage."}
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <GlowCard accent="cyan" testId="card-precision-verified">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="w-6 h-6 text-cyan-400" style={{ filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.7))' }} />
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest uppercase bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-400/40"
                style={{ boxShadow: '0 0 8px rgba(34,211,238,0.4)' }}>{T('Vérifié', 'Verified')}</span>
            </div>
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-2">{T('Source officielle', 'Official source')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T("Les produits emballés (codes-barres, marques canadiennes) matchent en quelques ms. Le badge cyan signale « source officielle ».", 'Packaged products match in milliseconds. Cyan badge = official source.')}</p>
          </GlowCard>
          <GlowCard accent="cyan" testId="card-precision-leaderboard">
            <div className="flex items-center gap-3 mb-3">
              <Crown className="w-6 h-6 text-cyan-400" style={{ filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.7))' }} />
              <span className="text-[10px] font-mono tracking-widest text-cyan-300/80">/leaderboard</span>
            </div>
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-2">{T('Top Précision 🇨🇦', 'Top Precision 🇨🇦')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T("Classement des utilisateurs par % de repas vérifiés. Pousse à la rigueur sans tomber dans l'obsession.", 'Users ranked by verified-meals percentage. Pushes precision without obsession.')}</p>
          </GlowCard>
        </div>
      </div>
    </section>
  );
}

function SectionBadges({ fr, navigate }) {
  const stats = [
    { label: T('Marche', 'Walking'),  value: '22', accent: 'cyan' },
    { label: T('Carburant', 'Fuel'),     value: '5',  accent: 'orange' },
    { label: T('Forge', 'Forge'),    value: '4',  accent: 'red' },
    { label: T('Citadelle', 'Citadel'),  value: '3',  accent: 'emerald' },
    { label: T('Grades', 'Ranks'),    value: '5',  accent: 'gold' },
  ];
  return (
    <section className="relative py-16 px-4 border-t border-zinc-900/50" data-testid="presentation-section-badges">
      <div className="max-w-5xl mx-auto">
        <SectionLabel accent="gold">04 · 39 Badges Tactiques</SectionLabel>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mt-4 mb-3">
          {T("Du Cadet à l'Opération Apollo.", 'From Cadet to Operation Apollo.')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
          {fr
            ? "Chaque log déclenche une évaluation des 39 critères. Du badge « Cadet de la Marche » au légendaire Operation Apollo (500 millions de pas lifetime), chaque déblocage génère une image OG 1200×630 prête à partager. Triple Threat Leaderboard divise tes points en 3 catégories (Discipline, Sommet, Éclaireur) avec 4 grades : Recrue → Gardien → Élite → Légende."
            : "Each log evaluates all 39 criteria. From 'Walking Cadet' to legendary Operation Apollo (500M lifetime steps), each unlock generates a shareable 1200×630 OG image. Triple Threat Leaderboard splits points across 3 categories with 4 ranks."}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {stats.map((s) => (
            <GlowCard key={s.label} accent={s.accent} testId={`badge-stat-${s.label.toLowerCase()}`}>
              <div className="text-3xl font-black tabular-nums text-foreground mb-1"
                style={{ textShadow: `0 0 12px var(--tw-shadow-color)` }}>
                {s.value}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{s.label}</div>
            </GlowCard>
          ))}
        </div>
        <Button data-testid="presentation-badges-cta" variant="outline" onClick={() => navigate('/victories')}
          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/15 uppercase tracking-widest text-xs font-black">
          {T('Voir mes badges', 'View my badges')} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </section>
  );
}

function SectionModes({ fr }) {
  const modes = [
    { icon: Stethoscope, t: T('Clinique', 'Clinical'),  d: T('Mode blessure : calories ↓ + nutriments réparation ↑', 'Injury mode: calories ↓ + repair nutrients ↑'), accent: 'emerald' },
    { icon: Heart,       t: T('Hormonal', 'Hormonal'),  d: T('Cycle menstruel, périménopause, optim. cortisol/insuline', 'Menstrual cycle, perimenopause, cortisol/insulin optim.'), accent: 'rose' },
    { icon: Moon,        t: T('Religieux', 'Religious'), d: T('Ramadan, Halal, Kasher, Carême, Hindou — filtres auto.', 'Ramadan, Halal, Kosher, Lent, Hindu — auto filters.'),   accent: 'gold' },
    { icon: Brain,       t: T('Neuro', 'Neuro'),     d: T('Régime MIND pour aînés (-53% risque déclin cognitif)', 'MIND diet for seniors (-53% cognitive decline risk)'),   accent: 'violet' },
  ];
  return (
    <section className="relative py-16 px-4 border-t border-zinc-900/50" data-testid="presentation-section-modes">
      <div className="max-w-5xl mx-auto">
        <SectionLabel accent="emerald">05 · Modes adaptatifs</SectionLabel>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mt-4 mb-3">
          {T("L'IA s'adapte à TOI. Pas l'inverse.", 'AI adapts to YOU. Not the other way around.')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
          {T("Active un ou plusieurs modes selon ta vie. YUMIX ajustera macros, recettes, aubaines filtrées et même son langage. Aucune autre app ne couvre ce spectre.", "Activate one or more modes based on your life. YUMIX adjusts macros, recipes, filtered deals and even its language.")}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {modes.map((m) => (
            <GlowCard key={m.t} accent={m.accent} testId={`mode-${m.t.toLowerCase()}`}>
              <div className="flex items-start gap-3">
                <m.icon className={`w-6 h-6 flex-shrink-0`} style={{ filter: `drop-shadow(0 0 6px var(--tw-shadow-color))` }} />
                <div>
                  <h3 className="font-heading text-base uppercase tracking-tight font-black mb-1">{m.t}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{m.d}</p>
                </div>
              </div>
            </GlowCard>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionCoach({ fr, navigate }) {
  return (
    <section className="relative py-16 px-4 border-t border-zinc-900/50" data-testid="presentation-section-coach">
      <div className="max-w-5xl mx-auto">
        <SectionLabel accent="orange">06 · Coach YUMIX — Friction Zéro</SectionLabel>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mt-4 mb-3">
          {T("Parle. Il agit.", 'Talk. It acts.')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
          {fr
            ? "Photo de l'assiette, ou mode voix. Dis-lui « Je pèse 184.5 lbs » → bouton orange « Enregistrer 184.5 lbs » apparaît, 1 clic, c'est sauvé. Il connaît toute la map de l'app, les modes que tu as activés (jeûne, Ramadan, blessure...), et ne te suggère JAMAIS de manger pendant ta fenêtre de jeûne."
            : "Plate photo or voice mode. Say 'I weigh 184.5 lbs' → an orange 'Save 184.5 lbs' button appears, 1 tap, done. It knows the entire app map, your active modes (fasting, Ramadan, injury…), and NEVER suggests eating during your fasting window."}
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <GlowCard accent="orange" testId="card-coach-photo">
            <ChefHat className="w-6 h-6 text-orange-400 mb-3" style={{ filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.7))' }} />
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-1">{T('Photo Repas', 'Plate Photo')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T('Snap → analyse + macros + verdict 4 piliers en 1 prompt.', 'Snap → analysis + macros + 4-pillar verdict in 1 prompt.')}</p>
          </GlowCard>
          <GlowCard accent="orange" testId="card-coach-voice">
            <Mic className="w-6 h-6 text-orange-400 mb-3" style={{ filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.7))' }} />
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-1">{T('Mode Voix', 'Voice Mode')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T('Whisper STT (fr-CA) + voix de retour. Coach dans ta poche.', 'Whisper STT + spoken response. Coach in your pocket.')}</p>
          </GlowCard>
          <GlowCard accent="orange" testId="card-coach-actions">
            <MessageSquare className="w-6 h-6 text-orange-400 mb-3" style={{ filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.7))' }} />
            <h3 className="font-heading text-base uppercase tracking-tight font-black mb-1">{T('Boutons Action', 'Action Buttons')}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{T("1 clic → page pré-remplie. Allowlist sécurisée.", '1 tap → pre-filled page. Secure allowlist.')}</p>
          </GlowCard>
        </div>
        <div className="mt-8">
          <Button data-testid="presentation-coach-cta" onClick={() => navigate('/coach-ai')}
            className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-black uppercase tracking-widest text-xs px-6 py-5 rounded-full"
            style={{ boxShadow: '0 0 18px rgba(251,146,60,0.55)' }}>
            <Mic className="w-4 h-4 mr-2" />
            {T("Parler à l'Assistant", 'Talk to the Assistant')}
          </Button>
        </div>
      </div>
    </section>
  );
}

function SectionEcosystem({ fr }) {
  // Iter. 411.q — "Aubaines circulaires QC + Budget Eco" retiré : feature
  // Québec-only, ne s'applique pas au lancement Google Play / App Store
  // international. Réintroduire seulement pour la version QC beta.
  const items = [
    { icon: Footprints,  t: T('Centre de Commandement Marche', 'Walking Command Center') },
    { icon: BookOpen,    t: T('Recettes IA + Mes Recettes', 'AI Recipes + My Recipes') },
    { icon: Users,       t: T('Profil Familial (portions ajustées)', 'Family Profile (auto portions)') },
    { icon: ShieldAlert, t: T('Zéro Relais Legal Shield (sécurité 18+)', 'Zero Relay Legal Shield (18+ safety)') },
    { icon: Globe,       t: T('Communauté Extra Miles + Clash des Meutes', 'Extra Miles community + Pack Clash') },
  ];
  return (
    <section className="relative py-16 px-4 border-t border-zinc-900/50" data-testid="presentation-section-ecosystem">
      <div className="max-w-5xl mx-auto">
        <SectionLabel accent="rose">07 · Reste de l'écosystème</SectionLabel>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mt-4 mb-3">
          {T("Tout est connecté.", 'Everything is wired together.')}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(({ icon: Icon, t }, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-zinc-950/60 ring-1 ring-zinc-800/80 hover:ring-rose-400/40 transition-all">
              <Icon className="w-4 h-4 text-rose-400 flex-shrink-0" style={{ filter: 'drop-shadow(0 0 4px rgba(244,114,182,0.6))' }} />
              <span className="text-sm text-foreground/85">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionFooter({ fr, navigate }) {
  return (
    <section className="relative py-20 px-4 border-t border-zinc-900/50">
      <div className="max-w-3xl mx-auto text-center">
        <SectionLabel accent="orange">{T('Prêt ?', 'Ready?')}</SectionLabel>
        <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-foreground mt-4 mb-4">
          {T("Mission #01 — démarrage.", 'Mission #01 — boot.')}
        </h2>
        <p className="text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
          {fr
            ? "14 jours d'essai. Pas de carte. Si ce n'est pas pour toi, tu fermes l'app. Si c'est pour toi, ton premier badge tombe au jour 7."
            : '14-day trial. No card. If it\'s not for you, close the app. If it is, your first badge drops on day 7.'}
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button data-testid="presentation-footer-start" onClick={() => navigate('/')}
            className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-black uppercase tracking-widest text-xs px-8 py-6 rounded-full"
            style={{ boxShadow: '0 0 24px rgba(251,146,60,0.6)' }}>
            {T("Démarrer l'expérience", 'Start the experience')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button data-testid="presentation-footer-guide" onClick={() => navigate('/guide')}
            variant="outline"
            className="border-orange-500/50 text-orange-300 hover:bg-orange-500/15 font-bold uppercase tracking-widest text-xs px-8 py-6 rounded-full">
            <Compass className="w-4 h-4 mr-2" />
            {T('Lire le Guide', 'Read the Guide')}
          </Button>
        </div>
        <p className="mt-10 text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60 font-mono">
          {T('YUMIX · Powered by Extra Miles · Made in Québec', 'YUMIX · Powered by Extra Miles · Made in Québec')}
        </p>
      </div>
    </section>
  );
}

// ───── Page root ─────

const Presentation = () => {
  const navigate = useNavigate();
  // Detect lang from localStorage (App default = fr). Public route, so don't depend on AuthContext.
  const [lang, setLang] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('language')) || 'fr');
  const fr = lang === 'fr';
  _pageLang = lang; // Iter. 411.r

  // Capture ?ref=<id> on mount, then reflect the stored ref in state for the
  // invitation ribbon. TTL: 30 days (cleared on next visit if expired).
  const [refId, setRefId] = useState(null);
  const [shareState, setShareState] = useState('idle'); // 'idle' | 'copied'
  useEffect(() => {
    const captured = captureRefFromUrl();
    setRefId(captured || getStoredRef());
  }, []);

  const handleShare = async () => {
    // Use the existing referrer (or fallback to a generic 'demo' code so the
    // share link still works for anonymous visitors).
    const id = refId || 'demo';
    const res = await shareYumix({ refId: id, fr });
    if (res.method === 'clipboard' && res.ok) {
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2400);
    }
    // 'share' method opens the native sheet — no UI change needed.
  };

  return (
    <div className="min-h-screen bg-black text-foreground" data-testid="presentation-page">
      {/* Sticky top bar — minimal */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-black/70 border-b border-zinc-900/60">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="presentation-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-zinc-200 to-zinc-400 flex items-center justify-center"
              style={{ boxShadow: '0 0 14px rgba(226,232,240,0.55), 0 0 4px rgba(244,114,182,0.4)' }}>
              <span className="text-black font-black text-xs">Y</span>
            </div>
            <span className="font-heading text-sm uppercase tracking-[0.25em] text-foreground font-black">YUMIX</span>
          </button>
          <div className="flex items-center gap-2">
            {/* Iter. 411.q — Selector 3-way FR/EN/ES rendu plus visible
                (avant : simple toggle FR↔EN). Sauve `language` en
                localStorage — les traductions ES manquantes tombent
                gracieusement sur EN dans le reste de l'app. */}
            <div
              className="flex items-center rounded-full bg-black/70 ring-1 ring-orange-400/40 p-0.5"
              style={{ boxShadow: '0 0 10px rgba(251,146,60,0.25)' }}
              data-testid="presentation-lang-switcher"
            >
              {['fr', 'en', 'es'].map((code) => {
                const active = lang === code;
                return (
                  <button
                    key={code}
                    onClick={() => {
                      setLang(code);
                      try { localStorage.setItem('language', code); } catch { /* ignore */ }
                    }}
                    data-testid={`presentation-lang-${code}`}
                    className={`text-[10px] font-mono font-black tracking-widest px-2.5 py-1 rounded-full transition-all ${
                      active
                        ? 'bg-orange-500 text-black shadow-[0_0_10px_rgba(251,146,60,0.7)]'
                        : 'text-foreground/70 hover:text-orange-300'
                    }`}
                  >
                    {code.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <Button data-testid="presentation-top-cta" onClick={() => navigate('/')}
              className="bg-orange-500 hover:bg-orange-400 text-white font-black uppercase tracking-widest text-[10px] px-4 py-2 rounded-full"
              style={{ boxShadow: '0 0 14px rgba(251,146,60,0.5)' }}>
              {T('Démarrer', 'Start')}
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      <SectionHero      navigate={navigate} fr={fr} lang={lang} refId={refId} onShare={handleShare} shareState={shareState} />
      <SectionMission   fr={fr} />
      <SectionMindset   fr={fr} />
      <SectionPrecision fr={fr} />
      <SectionBadges    fr={fr} navigate={navigate} />
      <SectionModes     fr={fr} />
      <SectionCoach     fr={fr} navigate={navigate} />
      <SectionEcosystem fr={fr} />
      <SectionFooter    fr={fr} navigate={navigate} />
    </div>
  );
};

export default Presentation;
